import { useEffect, useState } from 'react';
import api from '@/lib/api';

interface FreightQuote {
  carrier: string;
  price: number;
  deliveryDays: number;
}

interface Carrier {
  id: string;
  name: string;
  code: string;
}

export default function FreightPage() {
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [form, setForm] = useState({
    orderId: '',
    weight: 0,
    length: 0,
    width: 0,
    height: 0,
    originZip: '',
    destinationZip: '',
  });
  const [quotes, setQuotes] = useState<FreightQuote[]>([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    api.get('/carriers').then((r) => setCarriers(r.data)).catch(() => setCarriers([]));
  }, []);

  const getQuotes = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoadingQuotes(true);
    setShowResults(false);
    try {
      const r = await api.post('/freight/quote', form);
      setQuotes(r.data);
      setShowResults(true);
    } catch {
      setQuotes([]);
    }
    setLoadingQuotes(false);
  };

  const selectQuote = async (quote: FreightQuote) => {
    await api.post('/freight/select', {
      orderId: form.orderId,
      carrier: quote.carrier,
      price: quote.price,
      deliveryDays: quote.deliveryDays,
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-dark-100">
          <h3 className="text-sm font-semibold text-dark-500 uppercase mb-4">Solicitar Cotacao</h3>
          <form onSubmit={getQuotes} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-dark-700 mb-1">Pedido ID</label>
              <input
                type="text"
                value={form.orderId}
                onChange={(e) => setForm({ ...form, orderId: e.target.value })}
                className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-dark-700 mb-1">Peso (kg)</label>
              <input
                type="number"
                step="0.01"
                value={form.weight}
                onChange={(e) => setForm({ ...form, weight: parseFloat(e.target.value) })}
                className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Comprimento</label>
                <input
                  type="number"
                  value={form.length}
                  onChange={(e) => setForm({ ...form, length: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Largura</label>
                <input
                  type="number"
                  value={form.width}
                  onChange={(e) => setForm({ ...form, width: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">Altura</label>
                <input
                  type="number"
                  value={form.height}
                  onChange={(e) => setForm({ ...form, height: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">CEP Origem</label>
                <input
                  type="text"
                  value={form.originZip}
                  onChange={(e) => setForm({ ...form, originZip: e.target.value })}
                  className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-700 mb-1">CEP Destino</label>
                <input
                  type="text"
                  value={form.destinationZip}
                  onChange={(e) => setForm({ ...form, destinationZip: e.target.value })}
                  className="w-full px-3 py-2 border border-dark-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>
            </div>
            <button
              type="submit"
              className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              {loadingQuotes ? 'Calculando...' : 'Cotar Frete'}
            </button>
          </form>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-dark-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-dark-100">
            <h3 className="text-sm font-semibold text-dark-500 uppercase">Resultados</h3>
          </div>
          {showResults ? (
            <table className="w-full">
              <thead className="bg-dark-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">Transportadora</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-dark-500 uppercase">Preco</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-dark-500 uppercase">Prazo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-dark-500 uppercase">Acao</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-100">
                {quotes.map((quote, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-dark-50/50'}>
                    <td className="px-6 py-4 text-sm font-medium text-dark-900">{quote.carrier}</td>
                    <td className="px-6 py-4 text-sm font-medium text-dark-900 text-right">
                      {quote.price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </td>
                    <td className="px-6 py-4 text-sm text-dark-600 text-right">{quote.deliveryDays} dias</td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => selectQuote(quote)}
                        className="px-3 py-1.5 bg-primary-600 text-white rounded-lg text-xs font-medium hover:bg-primary-700 transition-colors"
                      >
                        Selecionar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12 text-dark-500">
              {loadingQuotes ? 'Calculando cotacoes...' : 'Preencha o formulario para cotar'}
            </div>
          )}
        </div>
      </div>
  );
}
